import { useEffect, useState } from 'react';
import { useAuth0, useUserAuth, handleLogout } from '@lucid/auth';
import { useGetUserIdentity, isAuthError } from '../api/services/lupp/user-identity/user-identity';
import { useStore, getStoreState } from '../store/store';
import { getStorageItem, setStorageItem, removeStorageItem } from '../utils/tools';
import { DEFAULT_LOCALE } from '../config/constants';
import type { UserIdentity } from '../api/services/lupp/user-identity/dto.user-identity';

const USER_IDENTITY_STORAGE_KEY = 'lupp-user-identity';

/**
 * Build the storage key for a specific user
 * @param userId - The Auth0 user ID
 * @returns The full storage key
 */
const buildStorageKey = (userId: string): string => `${USER_IDENTITY_STORAGE_KEY}-${userId}`;

/**
 * Hook to fetch and store user identity after authentication
 * Should be used in the main App component or AuthGuard
 * Automatically calls /me endpoint when user is authenticated and updates global store
 * Caches user identity in localStorage to prevent duplicate API calls across page navigations
 *
 * @returns Object containing userIdentity, isLoading, error, and isSuccess
 * @example
 * ```typescript
 * function App() {
 *   useUserIdentitySync();
 *   // ... rest of app
 * }
 * ```
 */
export const useUserIdentitySync = () => {
  const {
    user,
    isLoading: isAuthLoading,
    accessToken,
  } = useUserAuth(`https://${import.meta.env.VITE_AUTH0_DOMAIN}/roles`);
  const { logout } = useAuth0();
  const {
    setUserIdentity,
    clearUserIdentity,
    userIdentity: storedUserIdentity,
    setLocale,
    setLanguage,
  } = useStore();
  const [hasCheckedCache, setHasCheckedCache] = useState(false);
  const [hasSetTokenLocale, setHasSetTokenLocale] = useState(false);

  const userId = user?.sub as string | undefined;

  const tokenLocale = (user?.locale as string | undefined)?.toLowerCase();

  // Set locale from JWT token on first authentication
  // Only runs once per session (guarded by hasSetTokenLocale) and only if
  // the store still has the default locale (i.e., user hasn't previously selected one)
  useEffect(() => {
    if (user?.sub && !hasSetTokenLocale) {
      const resolvedLocale = tokenLocale || DEFAULT_LOCALE;
      const currentLocale = getStoreState().locale;

      // Only apply JWT locale if store has the default value (no user preference persisted)
      if (currentLocale === DEFAULT_LOCALE) {
        setLocale(resolvedLocale);
        const langCode = resolvedLocale.split('-')[0];
        setLanguage(langCode !== 'en' ? 'en' : null);
      }
      setHasSetTokenLocale(true);
    }
  }, [user?.sub, tokenLocale, hasSetTokenLocale, setLocale, setLanguage]);

  // Check localStorage cache on mount when user is available
  useEffect(() => {
    if (userId && !storedUserIdentity && !hasCheckedCache) {
      const cached = getStorageItem<UserIdentity>(buildStorageKey(userId));
      if (cached) {
        setUserIdentity(cached);
      }
      setHasCheckedCache(true);
    }
  }, [userId, storedUserIdentity, hasCheckedCache, setUserIdentity]);

  // Only fetch user identity when:
  // 1. Auth is not loading (tokens have been fetched)
  // 2. User has a valid Auth0 sub (user ID)
  // 3. Access token exists (confirms auth is complete)
  // 4. User identity is not already in the store
  // 5. We've checked the cache and didn't find anything
  const isReadyForApiCall =
    !isAuthLoading &&
    !!userId &&
    userId.length > 0 &&
    !!accessToken &&
    !storedUserIdentity &&
    hasCheckedCache;

  const {
    data: userIdentity,
    isLoading,
    error,
    isSuccess,
  } = useGetUserIdentity({
    enabled: isReadyForApiCall,
    retry: 1,
    staleTime: Infinity, // Never consider stale - only fetch once
    refetchOnWindowFocus: false, // Prevent duplicate calls
    refetchOnMount: false, // Prevent duplicate calls on remount
  });

  // Update store and localStorage when user identity is successfully fetched
  useEffect(() => {
    if (isSuccess && userIdentity && userId) {
      setUserIdentity(userIdentity);
      setStorageItem(buildStorageKey(userId), userIdentity);
    }
  }, [isSuccess, userIdentity, userId, setUserIdentity]);

  // Clear user identity when user logs out
  useEffect(() => {
    if (!user && !isAuthLoading) {
      clearUserIdentity();
      if (userId) {
        removeStorageItem(buildStorageKey(userId));
      }
      setHasCheckedCache(false);
    }
  }, [user, isAuthLoading, userId, clearUserIdentity]);

  // Handle authentication errors - redirect to login with error message
  useEffect(() => {
    if (error && userId) {
      console.error('Failed to fetch user identity:', error);

      // Check if this is an authentication/authorization error
      if (isAuthError(error)) {
        // Clear cached data for this user
        removeStorageItem(buildStorageKey(userId));
        clearUserIdentity();

        // Use Auth0 logout to clear the SSO session before redirecting.
        // Without this, Auth0 auto-reauthenticates the user on the login page,
        // which calls /me again → 403 → redirect → infinite loop.
        handleLogout(logout, `${window.location.origin}/login?error=auth&reason=invalid-user`);
      }
    }
  }, [error, userId, clearUserIdentity, logout]);

  return {
    userIdentity: storedUserIdentity || userIdentity,
    isLoading,
    error,
    isSuccess,
  };
};
